import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  Flame,
  Trophy,
  Send,
  Check,
  Building2,
  Crosshair,
  ShieldCheck,
  Star
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* ──────────────────────────────────────────────────────────────
   CONFIG (WEBHOOK)
   Paste your Make.com Webhook URL here:
────────────────────────────────────────────────────────────── */
const MAKE_PLACEMENT_WEBHOOK_URL = "https://hook.eu2.make.com/9a6tkn4jbxw6jsj6crhu8f3xwambrs8p"; 

/* ──────────────────────────────────────────────────────────────
   MOCK DATA: PARTNERS & JOBS
────────────────────────────────────────────────────────────── */
const PROCESS_STEPS = [
  {
    step: "01",
    title: "The Scout",
    subtitle: "Submit Your Tape",
    desc: "Send us your application. We look at your experience, your attitude, and your goals. We only work with people who respect the craft.",
    icon: Crosshair,
  },
  {
    step: "02",
    title: "The Trial",
    subtitle: "Prove Your Worth",
    desc: "If you're a fit, we evaluate your skills. Need polish? We run you through a quick Academy boot camp to get your technique up to the D10 standard.",
    icon: Flame,
  },
  {
    step: "03",
    title: "The Transfer",
    subtitle: "Sign The Contract",
    desc: "We place you in a top-tier partner pizzeria. We negotiate the fit, ensure the standards are high, and watch you dominate the service.",
    icon: Trophy,
  },
];

const PARTNER_JOBS = [
  {
    id: "frankos-zagreb-pizzaiolo",
    pizzeria: "Franko's Pizza & Bar",
    location: "Zagreb, Croatia",
    role: "Junior Pizzaiolo",
    type: "Full Time",
    salary: "Competitive",
    desc: "Franko's Pizza & Bar in Zagreb is looking for a pizzaiolo to join the team in one of Croatia’s most recognized pizza destinations. A great fit for someone who wants to work in a fast-paced environment focused on quality, consistency, and serious pizza culture.",
    reqs: ["High Volume Exp", "Team Player", "Passionate"],
    logoUrl: "/img/academy/partners/frankos.jpg",
    logoText: "FR",
  },
  {
    id: "picasso-split-pizzaiolo",
    pizzeria: "Picasso",
    location: "Split, Croatia",
    role: "Junior Pizzaiolo",
    type: "Full Time",
    desc: "Picasso in Split is looking for a pizzaiolo to join the kitchen team. This is a strong opportunity for someone who enjoys working in a busy coastal setting and wants to be part of a pizzeria focused on delivering solid pizza every service.",
    reqs: ["Coastal Pace", "Dough Handling"],
    logoUrl: "/img/academy/partners/picasso.jpg",
    logoText: "PI",
  },
  {
    id: "pizzeria14-zagreb-pizzaiolo",
    pizzeria: "Pizzeria 14",
    location: "Zagreb, Croatia",
    role: "Junior Pizzaiolo",
    type: "Full Time",
    desc: "Pizzeria 14 in Zagreb is looking for a pizzaiolo to join its team. The role suits someone who is comfortable working with dough, handling service rhythm, and contributing to a kitchen that values reliability, pace, and well-executed pizza.",
    reqs: ["Reliability", "Service Rhythm"],
    logoUrl: "/img/academy/partners/pizzeria14.jpg",
    logoText: "14",
  },
  {
    id: "porta-salina-pag-pizzaiolo",
    pizzeria: "Porta Salina",
    location: "Pag, Croatia",
    role: "Junior Pizzaiolo",
    type: "Full Time",
    desc: "Porta Salina in Pag is looking for a pizzaiolo to join the team on the island. A strong option for someone who wants to work in a seasonal hospitality environment, be part of a well-known local spot, and help deliver quality pizza in a unique coastal setting.",
    reqs: ["Seasonal Focus", "Consistent Quality"],
    logoUrl: "/img/academy/partners/portasalina.jpg",
    logoText: "PS",
  },
];

const PARTNER_LOGOS = [
  "Franko's Pizza", "Picasso Split", "Pizzeria 14", "Porta Salina"
];

/* ──────────────────────────────────────────────────────────────
   ANIMATION
────────────────────────────────────────────────────────────── */
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ y: 30, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5, delay, ease: "easeOut" }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ──────────────────────────────────────────────────────────────
   HERO
────────────────────────────────────────────────────────────── */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div
      ref={ref}
      className="relative h-[85vh] min-h-[600px] bg-[#002B5C] overflow-hidden flex flex-col justify-center items-center px-4 text-center"
    >
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img
          src="/img/academy/d10pizza-academy-hero.jpeg"
          alt="D10 Network"
          className="w-full h-full object-cover mix-blend-overlay opacity-60 grayscale"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#002B5C]/90 via-[#002B5C]/70 to-[#002B5C]" />
      </motion.div>

      <div className="relative z-10 max-w-4xl mx-auto pt-20">
        <Reveal>
          <div className="inline-flex items-center gap-2 bg-[#FFD700] px-6 py-2 rounded-full mb-8 shadow-lg">
            <ShieldCheck size={16} className="text-[#002B5C]" />
            <span className="font-manrope font-bold text-[#002B5C] uppercase text-xs tracking-[0.2em]">
              D10 Partner Network
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h1 className="font-anton text-[12vw] sm:text-[9vw] md:text-7xl lg:text-8xl leading-[0.85] text-white uppercase mb-6 drop-shadow-xl">
            The Transfer <br />
            <span className="text-[#0087E6]">Market</span>
          </h1>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="font-serif italic text-white/80 text-xl sm:text-2xl md:text-3xl max-w-2xl mx-auto leading-relaxed mb-10">
            "We scout the best talent and place them in the most demanding kitchens. Play in the big leagues."
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => scrollTo("contract")}
              className="bg-white text-[#002B5C] font-anton uppercase tracking-widest text-lg py-4 px-10 rounded-full shadow-xl shadow-blue-900/50 hover:bg-[#FFD700] transition-all duration-300"
            >
              Enter The Draft
            </button>
            <button
              onClick={() => scrollTo("jobs")}
              className="bg-[#0087E6] text-white font-anton uppercase tracking-widest text-lg py-4 px-10 rounded-full shadow-xl shadow-blue-900/50 hover:bg-white hover:text-[#002B5C] transition-all duration-300 flex items-center justify-center gap-2"
            >
              View Open Roles <ArrowRight size={18} />
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   PARTNER LOGOS MARQUEE
────────────────────────────────────────────────────────────── */
const PartnerSlider = () => {
  return (
    <div className="bg-[#0F172A] py-6 border-y border-white/10 overflow-hidden flex relative">
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0F172A] to-transparent z-10" />
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0F172A] to-transparent z-10" />
      
      <motion.div
        className="flex whitespace-nowrap items-center gap-12 sm:gap-24 px-12"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ repeat: Infinity, ease: "linear", duration: 25 }}
      >
        {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map((partner, index) => (
          <div key={index} className="flex items-center gap-3 opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0">
            <Star size={14} className="text-[#FFD700]" />
            <span className="font-anton text-2xl text-white uppercase tracking-wider">{partner}</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   HOW IT WORKS
────────────────────────────────────────────────────────────── */
const HowItWorks = () => {
  return (
    <section className="py-24 bg-white text-[#002B5C] relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Reveal>
            <span className="text-[#0087E6] font-bold tracking-[0.2em] uppercase text-xs bg-[#F0F9FF] px-4 py-2 rounded-full">
              The Agent Process
            </span>
            <h2 className="font-anton text-5xl md:text-6xl uppercase mt-6 mb-4">
              How We Draft You
            </h2>
            <p className="font-manrope text-gray-500 text-lg">
              We act as your agent. We verify your skills, protect your value, and connect you with serious owners who respect the craft.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 md:gap-12 relative">
          <div className="hidden lg:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-gray-200 border-t-2 border-dashed border-gray-300 z-0" />

          {PROCESS_STEPS.map((step, i) => (
            <Reveal key={i} delay={i * 0.15}>
              <div className="relative z-10 bg-white border border-gray-100 p-8 rounded-3xl hover:border-[#0087E6]/30 hover:shadow-2xl transition-all duration-500 group h-full">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-anton text-6xl text-gray-100 group-hover:text-[#F0F9FF] transition-colors">
                    {step.step}
                  </span>
                  <div className="w-16 h-16 bg-[#F0F9FF] rounded-2xl flex items-center justify-center group-hover:bg-[#0087E6] group-hover:-translate-y-2 transition-all duration-300">
                    <step.icon className="text-[#0087E6] group-hover:text-white transition-colors" size={28} />
                  </div>
                </div>

                <h3 className="font-anton text-3xl uppercase mb-1">{step.title}</h3>
                <p className="font-serif italic text-[#0087E6] mb-4">{step.subtitle}</p>
                <p className="font-manrope text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   JOB BOARD (PARTNER LISTINGS)
────────────────────────────────────────────────────────────── */
const JobMarket = ({ onApply }) => {
  return (
    <section id="jobs" className="py-24 bg-[#F0F9FF]">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <Reveal>
              <span className="text-[#0087E6] font-bold uppercase tracking-widest text-xs">
                Active Listings
              </span>
              <h2 className="font-anton text-5xl md:text-6xl text-[#002B5C] uppercase mt-4">
                The League Board
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-4 bg-white px-6 py-4 rounded-2xl border border-gray-100 shadow-sm">
              <div className="text-center">
                <div className="font-anton text-2xl text-[#0087E6]">{PARTNER_JOBS.length}</div>
                <div className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Open Roles</div>
              </div>
              <div className="w-px h-10 bg-gray-200"></div>
              <div className="text-center">
                <div className="font-anton text-2xl text-[#002B5C]">100%</div>
                <div className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Verified Fits</div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="grid gap-6">
          {PARTNER_JOBS.map((job, index) => (
            <Reveal key={job.id} delay={index * 0.1}>
              <div className="bg-white rounded-3xl p-6 md:p-8 border border-gray-100 hover:border-[#0087E6]/30 hover:shadow-xl transition-all duration-300 flex flex-col md:flex-row gap-8">
                
                <div className="md:w-1/3 border-b md:border-b-0 md:border-r border-gray-100 pb-6 md:pb-0 md:pr-8 flex flex-col items-center justify-center text-center">
                  
                  <div className="w-28 h-28 bg-white border border-gray-200 rounded-2xl flex items-center justify-center font-anton text-4xl tracking-wider flex-shrink-0 overflow-hidden shadow-sm mb-6">
                    {job.logoUrl ? (
                      <img 
                        src={job.logoUrl} 
                        alt={`${job.pizzeria} logo`} 
                        className="w-full h-full object-contain p-2" 
                      />
                    ) : (
                      <span className="text-[#002B5C]">{job.logoText}</span>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="font-anton text-2xl uppercase text-[#002B5C] leading-tight mb-2">{job.pizzeria}</h3>
                    {job.location && (
                      <div className="flex items-center justify-center gap-1 text-gray-500 mt-1">
                        <MapPin size={12} className="text-[#0087E6]" />
                        <span className="text-xs font-bold uppercase tracking-wider">{job.location}</span>
                      </div>
                    )}
                  </div>

                  <div className="inline-flex items-center gap-2 bg-[#F0F9FF] text-[#0087E6] px-3 py-1.5 rounded-lg w-max">
                    <Building2 size={14} />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Official Partner</span>
                  </div>
                </div>

                <div className="md:w-2/3 flex flex-col justify-between pt-4 md:pt-0">
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                      <h4 className="font-anton text-3xl uppercase text-[#002B5C]">{job.role}</h4>
                      {job.type && (
                        <span className="bg-[#002B5C] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
                          {job.type}
                        </span>
                      )}
                    </div>
                    
                    {job.salary && (
                      <p className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">{job.salary}</p>
                    )}

                    <p className={`font-manrope text-gray-600 leading-relaxed ${job.reqs?.length ? 'mb-6' : 'mb-8'}`}>{job.desc}</p>
                    
                    {job.reqs && job.reqs.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-8">
                        {job.reqs.map((req, i) => (
                          <span key={i} className="flex items-center gap-1.5 text-xs font-bold text-[#002B5C] bg-gray-50 px-3 py-1.5 rounded-md border border-gray-100">
                            <Check size={12} className="text-[#00D4AA]" /> {req}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => onApply(`${job.pizzeria} - ${job.role}`)}
                    className="w-full md:w-auto self-start px-8 py-4 bg-[#0087E6] text-white font-anton uppercase tracking-widest rounded-xl hover:bg-[#002B5C] transition-colors shadow-lg flex items-center justify-center gap-2 group"
                  >
                    Apply For Transfer <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   DUAL-MODE APPLICATION FORM
────────────────────────────────────────────────────────────── */
const PlacementForm = ({ targetJob }) => {
  const [formMode, setFormMode] = useState("player"); // "player" | "club"

  // Roles available for Multi-Select Checkboxes
  const allRoles = useMemo(() => [
    ...PARTNER_JOBS.map(j => `${j.pizzeria} - ${j.role}`),
    "Open Free Agent (Network)"
  ], []);

  // Player Form State
  const [playerForm, setPlayerForm] = useState({
    name: "", phone: "", email: "", targets: [], experience: "", message: "",
  });

  // Club Form State
  const [clubForm, setClubForm] = useState({
    clubName: "", contactName: "", phone: "", email: "", location: "", openPositions: "", message: "",
  });

  const [company, setCompany] = useState(""); // Honeypot
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Update selected targets if user clicked "Apply" from a specific card
  useEffect(() => {
    if (targetJob) {
      setPlayerForm((prev) => ({
        ...prev,
        targets: prev.targets.includes(targetJob) ? prev.targets : [...prev.targets, targetJob]
      }));
      setFormMode("player");
    }
  }, [targetJob]);

  // Handle Multi-Select Toggling
  const handleTargetToggle = (role) => {
    setPlayerForm(prev => {
      const targets = prev.targets.includes(role)
        ? prev.targets.filter(t => t !== role) // Remove if already checked
        : [...prev.targets, role];             // Add if unchecked
      return { ...prev, targets };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setErrorMsg("");

    if (company) { setSuccess(true); return; }

    // Validation
    if (formMode === "player" && playerForm.targets.length === 0) {
      setErrorMsg("Please select at least one transfer target.");
      return;
    }

    if (!MAKE_PLACEMENT_WEBHOOK_URL || MAKE_PLACEMENT_WEBHOOK_URL.includes("INSERT_YOUR_MAKE_WEBHOOK_URL_HERE")) {
      setErrorMsg("Webhook URL missing. Please add it to your configuration.");
      return;
    }

    setSending(true);

    try {
      const payload = {
        type: formMode === "player" ? "Talent Application" : "Club Inquiry",
        data: formMode === "player" ? playerForm : clubForm,
        source: "D10 Transfer Market",
        page: typeof window !== "undefined" ? window.location.href : "",
        submittedAt: new Date().toISOString(),
      };

      const res = await fetch(MAKE_PLACEMENT_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Webhook failed (${res.status})`);

      setSuccess(true);
      if (formMode === "player") {
        setPlayerForm({ name: "", phone: "", email: "", targets: [], experience: "", message: "" });
      } else {
        setClubForm({ clubName: "", contactName: "", phone: "", email: "", location: "", openPositions: "", message: "" });
      }
    } catch (err) {
      setErrorMsg(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contract" className="py-24 bg-white">
      <div className="container mx-auto px-4 max-w-4xl">
        <Reveal>
          <div className="bg-white rounded-[2rem] p-8 md:p-16 border border-gray-200 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-0 h-0 border-t-[100px] border-r-[100px] border-t-[#0087E6] border-r-transparent z-0" />
            
            <div className="relative z-10 grid md:grid-cols-5 gap-12">
              <div className="md:col-span-2">
                <span className="text-[#0087E6] font-bold uppercase tracking-widest text-xs">
                  {formMode === "player" ? "Sign The Contract" : "Expand Your Squad"}
                </span>
                <h2 className="font-anton text-4xl lg:text-5xl text-[#002B5C] uppercase mt-2 mb-6 leading-tight">
                  {formMode === "player" ? "Enter The Draft" : "Scout Talent"}
                </h2>
                <p className="font-manrope text-gray-500 mb-8 leading-relaxed">
                  {formMode === "player" 
                    ? "Fill out your details. Tell us where you've played and what you can do. Our scouts review every application." 
                    : "Looking for top-tier staff? We train and scout the best in the business. Submit your requirements and let's build your team."}
                </p>
                
                {/* Industry-Specific Bullets */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-[#002B5C] font-bold text-sm">
                    <ShieldCheck className="text-[#00D4AA]" size={20} /> 
                    {formMode === "player" 
                      ? "Confidential — We don't alert your current employer" 
                      : "Vetted Roster — Every candidate is D10 tested"}
                  </div>
                  <div className="flex items-center gap-3 text-[#002B5C] font-bold text-sm">
                    <Check className="text-[#00D4AA]" size={20} /> 
                    {formMode === "player" ? "Direct intro to owners & head chefs" : "No upfront agency fees"}
                  </div>
                </div>
              </div>

              <div className="md:col-span-3">
                <div className="grid grid-cols-2 gap-2 p-1.5 bg-gray-100 rounded-2xl mb-8">
                  <button 
                    type="button"
                    onClick={() => { setFormMode("player"); setSuccess(false); setErrorMsg(""); }}
                    className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-xl transition-all duration-300 ${formMode === 'player' ? 'bg-[#0087E6] text-white shadow-md' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    <span className="font-anton uppercase tracking-widest text-sm md:text-base">Join a Top Kitchen</span>
                    <span className={`text-[10px] uppercase font-bold tracking-widest mt-1 ${formMode === 'player' ? 'text-white/80' : 'text-gray-400'}`}>For Pizzaiolos & Staff</span>
                  </button>
                  
                  <button 
                    type="button"
                    onClick={() => { setFormMode("club"); setSuccess(false); setErrorMsg(""); }}
                    className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-xl transition-all duration-300 ${formMode === 'club' ? 'bg-[#002B5C] text-white shadow-md' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    <span className="font-anton uppercase tracking-widest text-sm md:text-base">Hire D10 Talent</span>
                    <span className={`text-[10px] uppercase font-bold tracking-widest mt-1 ${formMode === 'club' ? 'text-white/80' : 'text-gray-400'}`}>For Pizzerias & Owners</span>
                  </button>
                </div>

                {/* Success Message */}
                {success && (
                  <div className="mb-8 rounded-2xl border border-[#00D4AA]/30 bg-[#00D4AA]/10 p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#00D4AA]/20 flex items-center justify-center flex-shrink-0">
                        <Check className="text-[#00D4AA]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#002B5C]">{formMode === "player" ? "Draft Application Sent." : "Inquiry Sent."}</p>
                        <p className="text-sm text-gray-600">Our team will review and reach out shortly.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Visible Error Message */}
                {errorMsg && (
                  <div className="mb-8 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-600 font-bold">
                    {errorMsg}
                  </div>
                )}

                <form onSubmit={submit} className="space-y-5">
                  <input type="text" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} className="hidden" aria-hidden="true" />

                  <AnimatePresence mode="wait">
                    {formMode === "player" ? (
                      <motion.div key="player" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="space-y-5">
                        <div className="grid md:grid-cols-2 gap-5">
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Full Name *</label>
                            <input type="text" required value={playerForm.name} onChange={(e) => setPlayerForm({ ...playerForm, name: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#0087E6] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all" disabled={sending} />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Phone *</label>
                            <input type="tel" required value={playerForm.phone} onChange={(e) => setPlayerForm({ ...playerForm, phone: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#0087E6] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all" disabled={sending} />
                          </div>
                        </div>

                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Email *</label>
                          <input type="email" required value={playerForm.email} onChange={(e) => setPlayerForm({ ...playerForm, email: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#0087E6] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all" disabled={sending} />
                        </div>

                        {/* Multi-Select Targets */}
                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Select Transfer Targets * (Pick multiple)</label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto p-2 bg-[#F0F9FF] border-2 border-transparent rounded-xl custom-scrollbar">
                            {allRoles.map(role => {
                              const isSelected = playerForm.targets.includes(role);
                              return (
                                <label key={role} className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'bg-[#0087E6] border-[#0087E6] text-white shadow-md' : 'bg-white border-gray-200 text-[#002B5C] hover:border-[#0087E6]/50'}`}>
                                  <input type="checkbox" className="hidden" checked={isSelected} onChange={() => handleTargetToggle(role)} disabled={sending} />
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center mr-3 flex-shrink-0 ${isSelected ? 'border-white bg-[#0087E6]' : 'border-gray-300'}`}>
                                    {isSelected && <Check size={12} className="text-white" />}
                                  </div>
                                  <span className="text-[11px] font-bold uppercase tracking-wider leading-tight">{role}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Experience Level *</label>
                          <select required value={playerForm.experience} onChange={(e) => setPlayerForm({ ...playerForm, experience: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#0087E6] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all cursor-pointer" disabled={sending}>
                            <option value="" disabled>Select...</option>
                            <option value="Rookie (0-1 Years)">Rookie (0-1 Years - Will need Academy)</option>
                            <option value="Starter (1-3 Years)">Starter (1-3 Years)</option>
                            <option value="Veteran (3-5+ Years)">Veteran (3-5+ Years)</option>
                            <option value="Master (Head Chef)">Master (Head Chef Level)</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">The Pitch (Why you?)</label>
                          <textarea rows={3} value={playerForm.message} onChange={(e) => setPlayerForm({ ...playerForm, message: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#0087E6] rounded-xl px-4 py-3 font-manrope text-[#002B5C] outline-none transition-all resize-none" placeholder="Tell us about your skills and goals..." disabled={sending} />
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div key="club" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-5">
                        <div className="grid md:grid-cols-2 gap-5">
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Pizzeria / Company Name *</label>
                            <input type="text" required value={clubForm.clubName} onChange={(e) => setClubForm({ ...clubForm, clubName: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#002B5C] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all" disabled={sending} />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Contact Name *</label>
                            <input type="text" required value={clubForm.contactName} onChange={(e) => setClubForm({ ...clubForm, contactName: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#002B5C] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all" disabled={sending} />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Phone *</label>
                            <input type="tel" required value={clubForm.phone} onChange={(e) => setClubForm({ ...clubForm, phone: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#002B5C] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all" disabled={sending} />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">City / Location *</label>
                            <input type="text" required value={clubForm.location} onChange={(e) => setClubForm({ ...clubForm, location: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#002B5C] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all" disabled={sending} />
                          </div>
                        </div>

                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Email *</label>
                          <input type="email" required value={clubForm.email} onChange={(e) => setClubForm({ ...clubForm, email: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#002B5C] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all" disabled={sending} />
                        </div>

                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Positions Needed</label>
                          <input type="text" value={clubForm.openPositions} onChange={(e) => setClubForm({ ...clubForm, openPositions: e.target.value })} placeholder="e.g. Lead Pizzaiolo, 2 Commis" className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#002B5C] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all" disabled={sending} />
                        </div>

                        <div>
                          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block mb-1">Requirements & Details</label>
                          <textarea rows={3} value={clubForm.message} onChange={(e) => setClubForm({ ...clubForm, message: e.target.value })} className="w-full bg-[#F0F9FF] border-2 border-transparent focus:bg-white focus:border-[#002B5C] rounded-xl px-4 py-3 font-manrope text-[#002B5C] outline-none transition-all resize-none" placeholder="What are the standards for your kitchen?" disabled={sending} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <button
                    type="submit"
                    disabled={sending}
                    className={`w-full py-5 font-anton uppercase tracking-widest text-xl rounded-xl shadow-xl transition-colors mt-4 flex items-center justify-center gap-3
                      ${sending ? "bg-gray-200 text-gray-500 cursor-not-allowed" : (formMode === "player" ? "bg-[#FFD700] text-[#002B5C] hover:bg-[#002B5C] hover:text-white" : "bg-[#002B5C] text-white hover:bg-[#0087E6]")}`}
                  >
                    <Send size={20} />
                    {sending ? "Processing..." : (formMode === "player" ? "Submit Application" : "Request Talent")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   MAIN PAGE ASSEMBLY
────────────────────────────────────────────────────────────── */
export default function PlacementPage() {
  const [selectedTarget, setSelectedTarget] = useState("");

  const handleApply = (role) => {
    setSelectedTarget(role);
    setTimeout(() => {
      document.getElementById("contract")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <div className="bg-white min-h-screen selection:bg-[#0087E6] selection:text-white overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; letter-spacing: 0.02em; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }
        html { scroll-behavior: smooth; }
        
        /* Optional: Clean scrollbar for the multi-select targets */
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #CBD5E1; border-radius: 10px; }
      `}</style>

      <Navbar />

      <main>
        <Hero />
        <PartnerSlider />
        <HowItWorks />
        <JobMarket onApply={handleApply} />
        <PlacementForm targetJob={selectedTarget} />
      </main>

      <Footer />
    </div>
  );
}